import React, { Suspense, lazy, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useStore } from "react-redux";
import { readStoredPlayer, resolveAuthStatus } from "../../../CustomHooks/useAuthStatus";
import { auth } from "../../../services/firebaseConfig";
import { newLookAnyOn } from "../../../utils/newLookState";
import { promoDue, recordShown } from "../../../utils/newLookPromoWeb";
import { UI_2027_SECTIONS, showsToggle } from "../../../utils/ui2027Flag";
import { allAccessOrganizers, allAccessUsers } from "../../organizer/forms/formSelectArrays";

// The New look pop-up on the web (SCO-3126): the app's page over the site, a beat after a main page loads, at most once
// a day while every section is still off (local storage only, Phil 2026-10-23). Inside the apps the native home shows it instead. Mounted once inside
// BrowserRouter. Spec: docs/redesign-2027/native-chrome.md §13.

const NewLookWebHost = lazy(() => import("./NewLookWebHost"));

const IN_APP =
  !!new URLSearchParams(window.location.search).get("platform") ||
  !!window.webkit?.messageHandlers ||
  typeof window.Android === "object";

// Player Profile, Find a Tournament, Find a Club, Seasons, the organizer dashboard, Groups, Notifications.
const MAIN_PAGE = /^\/(?:[^/]+\/player\/(?:profile|findtournament|findclub|seasons)|[^/]+\/organizer|groups|notifications)\/?$/;
// The organizer dashboard gets the organizer version (SCO-3194).
const ORG_PAGE = /^\/[^/]+\/organizer\/?$/;
const PLAYER_PAGE = /^\/[^/]+\/player\//;
// A shared link, a running tournament or a sign-in opens these pages with one of these params.
const DEEP_PARAM = /^(gameid|tournament|view|action|return|club|group|activity|season|division|token|register|checkout|join|pin)/i;

const BEAT_MS = 2500;

// Who is signed in to the page underneath, never a logged-out page (Phil 2026-10-28). Player and organizer logins are
// separate (useAuthStatus): the organizer page needs the organizer one (or its Firebase session), the player pages the
// player one, Groups / Notifications either.
const signedInIds = (state, pathname) => {
  const a = resolveAuthStatus({
    params: new URLSearchParams(window.location.search),
    player: state.player || {},
    organizer: state.organizer || {},
    stored: readStoredPlayer(),
  });
  const orgOn = a.organizerLoggedIn || (!!auth.currentUser && !state.organizer?.isLogout);
  const player = a.playerLoggedIn ? a.playerId : null;
  const org = orgOn ? state.organizer?.accountid || auth.currentUser?.uid || null : null;
  if (ORG_PAGE.test(pathname)) return { player: null, org };
  if (PLAYER_PAGE.test(pathname)) return { player, org: null };
  return { player, org };
};

// Only someone the settings would show a New look switch to (`toggleFor`), so the staff gate covers the pop-up too.
const seesSwitches = ({ player, org }) =>
  Object.keys(UI_2027_SECTIONS).some(
    (key) => showsToggle(key, player, allAccessUsers) || showsToggle(key, org, allAccessOrganizers)
  );

const modalOpen = (state) =>
  Object.values(state.modal || {}).some((m) => m && m.showModal === true) ||
  !!document.querySelector(".showModal, .showModalWeb, .MuiModal-root:not(.MuiPopover-root)");

export default function NewLookWebPromo() {
  const { pathname, search } = useLocation();
  const store = useStore();
  // null = closed, else { org }: which version, fixed when it opens.
  const [open, setOpen] = useState(null);
  // Signing in on the page (the login modal) checks again.
  const who = useSelector((s) => `${s.player?.userID || ""}|${s.organizer?.bearerToken ? s.organizer.accountid : ""}`);

  useEffect(() => {
    if (IN_APP || open || !MAIN_PAGE.test(pathname)) return undefined;
    if ([...new URLSearchParams(search).keys()].some((k) => DEEP_PARAM.test(k))) return undefined;
    const id = setTimeout(() => {
      const state = store.getState();
      const ids = signedInIds(state, pathname);
      // Signed out or another modal up: signing in or the next main page tries again.
      if ((!ids.player && !ids.org) || !seesSwitches(ids) || modalOpen(state) || newLookAnyOn() || !promoDue()) return;
      recordShown();
      setOpen({ org: ORG_PAGE.test(pathname) });
    }, BEAT_MS);
    return () => clearTimeout(id);
  }, [pathname, search, open, store, who]);

  if (!open) return null;
  return (
    <Suspense fallback={null}>
      <NewLookWebHost org={open.org} onClose={() => setOpen(null)} />
    </Suspense>
  );
}
